import { isPlatformBrowser } from '@angular/common';
import { AfterViewInit, Component, effect, ElementRef, EventEmitter, inject, Inject, Output, PLATFORM_ID, ViewChild } from '@angular/core';

import { GoogleMapsService, IGoogleMapsMarker } from '../../services/google-maps-loader.service';
import { ENVIRONMENT_CONFIG } from '../../providers/environment.provider';
import { IEnvironment } from '../../interfaces/environment.interface';

declare const google: any;

@Component({
  selector: 'peca-google-maps',
  templateUrl: './google-maps.component.html',
  styleUrl: './google-maps.component.scss',
})
export class GoogleMapComponent implements AfterViewInit {
  @Output()
  loaded = new EventEmitter<void>();

  @ViewChild('map')
  mapElement!: ElementRef;

  googleMapsService = inject(GoogleMapsService);
  platformId = inject(PLATFORM_ID);
  environment = inject<IEnvironment>(ENVIRONMENT_CONFIG);

  map: any;
  infoWindow: any;
  markerRegistry = new Map<IGoogleMapsMarker, any>();

  constructor() {
    // EFFECT 1: Monitora mudanças na lista de stops (Reatividade)
    effect(() => {
      const stops = this.googleMapsService.stops();

      // Só tenta renderizar se o mapa já estiver instanciado
      if (this.map) {
        this.renderMarkers(stops);
      }
    });

    // EFFECT 2: Monitora qual stop está ativo para abrir o modal
    effect(() => {
      const activeStop = this.googleMapsService.activeStop();

      if (activeStop && this.map && this.infoWindow) {
        this.openInfoWindow(activeStop);
      }
    });
  }

  async ngAfterViewInit() {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    try {
      await this.googleMapsService.load();
      this.initMap();
    } catch (error) {
      console.error('CRITICAL: Erro ao carregar Google Maps API.', error);
    }
  }

  /**
   * Inicializa a instância básica do mapa (sem marcadores)
   */
  initMap() {
    if (!this.mapElement?.nativeElement) {
      return;
    }

    this.map = new google.maps.Map(this.mapElement.nativeElement, {
      zoom: 14,
      center: { lat: -23.55, lng: -46.63 }, // Centro inicial padrão
      mapId: this.environment.GOOGLE_MAPS.MAP_STYLES_ID,
      disableDefaultUI: true,
      clickableIcons: false,
    });

    this.infoWindow = new google.maps.InfoWindow();

    // Emite o evento para o pai saber que pode enviar os dados agora
    // Isso disparará o update no signal 'stops', que acionará o effect acima
    this.loaded.next();
  }

  /**
   * Responsável por limpar e desenhar os marcadores na tela
   */
  private renderMarkers(stops: IGoogleMapsMarker[]) {
    // 1. Limpa marcadores existentes do mapa e do registro
    this.markerRegistry.forEach((marker) => (marker.map = null));
    this.markerRegistry.clear();

    if (!stops || stops.length === 0) {
      return;
    }

    const bounds = new google.maps.LatLngBounds();

    // 2. Cria os novos marcadores
    stops.forEach((stop, index) => {
      const markerView = this.createMarkerView(index + 1, stop.finalized);

      const marker = new google.maps.marker.AdvancedMarkerElement({
        map: this.map,
        position: stop.position,
        content: markerView,
        title: stop.name,
      });

      // Adiciona listener de clique
      markerView.addEventListener('click', () => {
        this.googleMapsService.openMarker(stop);
      });

      this.markerRegistry.set(stop, marker);
      bounds.extend(stop.position);
    });

    // 3. Ajusta o zoom
    this.map.fitBounds(bounds);

    // Ajuste fino se houver apenas 1 ponto (evita zoom excessivo)
    if (stops.length === 1) {
      this.map.setZoom(16);
    }
  }

  /**
   * Cria o elemento HTML DOM para o AdvancedMarker
   */
  private createMarkerView(index: number, finalized: boolean): HTMLElement {
    const container = document.createElement('div');
    const bubble = document.createElement('div');
    const numberSpan = document.createElement('span');
    const pointer = document.createElement('div');

    container.className = 'fancy-marker-container';
    bubble.className = `fancy-marker-bubble${finalized ? ' --is-finalized' : ''}`;

    numberSpan.className = 'fancy-marker-number';
    numberSpan.textContent = `${index}`;

    pointer.className = 'fancy-marker-pointer';

    bubble.appendChild(numberSpan);
    container.appendChild(bubble);
    container.appendChild(pointer);

    return container;
  }

  openInfoWindow(stop: IGoogleMapsMarker) {
    const marker = this.markerRegistry.get(stop);

    if (!marker) {
      return;
    }

    const contentString = `
      <div class="gmaps-custom-modal">
        <address>${stop.address}</address>
      </div>
    `;

    this.infoWindow.setContent(contentString);
    this.infoWindow.open({
      anchor: marker,
      map: this.map,
      pixelOffset: new google.maps.Size(0, -10),
    });
  }
}
